import React, { memo, useState, useRef, useCallback, useEffect } from 'react'
import type { SceneNode } from '../api/types'
import { titleCase } from '../utils/titleCase'
import styles from './NodeCard.module.css'

/**
 * Perceived-brightness check for a hex color (#RRGGBB) using YIQ luminance.
 * Used to decide whether to put light or dark text on a node fill — only
 * relevant for themes that fill nodes with their role color (Bauhaus/Mondrian).
 */
function isDarkFill(hex: string): boolean {
    if (!hex || !hex.startsWith('#') || hex.length !== 7) return false
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return false
    const yiq = (r * 299 + g * 587 + b * 114) / 1000
    return yiq < 128
}

export interface NodeCardProps {
    node: SceneNode
    isSelected: boolean
    isHovered: boolean
    isSimulating: boolean
    onNodeClick: (nodeId: string, shiftKey: boolean) => void
    onNodeDoubleClick?: (nodeId: string, newName?: string, oldName?: string) => void
    onNodeEnter: (nodeId: string) => void
    onNodeLeave: () => void
}

// ── Motif glyph renderers (mirror SvgRenderer.kt) ───────────────────

/**
 * Fan glyph as a filled SVG sector with leaf count inside.
 *
 * Mirrors `SvgRenderer.renderFanGlyph` in core/render. OUT fans open
 * east; IN fans open west. Half-angle scales with leaf count
 * `clamp(12° + leaves × 6°, 15°, 60°)` per Dunne–Shneiderman 2013
 * §"Glyph Design" p. 3249.
 */
function FanGlyph({ node, isSelected, onClick, onEnter, onLeave }: {
    node: SceneNode
    isSelected: boolean
    onClick: (e: React.MouseEvent) => void
    onEnter: () => void
    onLeave: () => void
}) {
    const leafCount = node.glyphCounts?.leaves ?? 0
    const isOut = node.shape === 'FAN_OUT'
    const r = Math.min(node.width, node.height) / 2
    const cx = node.x
    const cy = node.y
    const halfAngleDeg = Math.min(60, Math.max(15, 12 + leafCount * 6))
    const halfAngleRad = (halfAngleDeg * Math.PI) / 180
    const baseAngle = isOut ? 0 : Math.PI
    const a1 = baseAngle - halfAngleRad
    const a2 = baseAngle + halfAngleRad
    const p1x = cx + r * Math.cos(a1)
    const p1y = cy + r * Math.sin(a1)
    const p2x = cx + r * Math.cos(a2)
    const p2y = cy + r * Math.sin(a2)
    const largeArc = halfAngleDeg > 90 ? 1 : 0
    const path = `M ${cx} ${cy} L ${p1x} ${p1y} A ${r} ${r} 0 ${largeArc} 1 ${p2x} ${p2y} Z`

    const labelR = r * 0.62
    const labelX = cx + labelR * Math.cos(baseAngle)
    const labelY = cy + labelR * Math.sin(baseAngle)

    const stroke = isSelected ? 'var(--accent)' : node.strokeColor
    const sw = isSelected ? node.strokeWidth * 1.5 : node.strokeWidth

    return (
        <g
            className={`gnode ${styles.gnode}`}
            data-node-id={node.id}
            onClick={onClick}
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
            style={{ cursor: 'pointer' }}
        >
            <path d={path} fill={node.fillColor} fillOpacity={0.9} stroke={stroke} strokeWidth={sw} />
            <text x={labelX} y={labelY} fontSize={13} fontWeight={700}
                fill={node.textColor} textAnchor="middle" dominantBaseline="central">
                {leafCount}
            </text>
            {node.displayName && (
                <text x={cx} y={cy - r - 6} fontSize={10} fontWeight={600}
                    fill={node.strokeColor} textAnchor="middle" opacity={0.85}>
                    {node.displayName}
                </text>
            )}
        </g>
    )
}

/**
 * Connector glyph as a tapered (symmetric) diamond with `D × K` inside.
 *
 * Mirrors `SvgRenderer.renderConnectorGlyph`. Diamond's long axis is
 * horizontal; the two pointy ends are attachment points for incoming /
 * outgoing edges. Size scales linearly with (spans + anchors).
 */
function ConnectorGlyph({ node, isSelected, onClick, onEnter, onLeave }: {
    node: SceneNode
    isSelected: boolean
    onClick: (e: React.MouseEvent) => void
    onEnter: () => void
    onLeave: () => void
}) {
    const anchors = node.glyphCounts?.anchors ?? 0
    const spans = node.glyphCounts?.spans ?? 0
    const w = node.width
    const h = node.height
    const cx = node.x
    const cy = node.y
    const leftX = cx - w / 2
    const rightX = cx + w / 2
    const topY = cy - h / 2
    const botY = cy + h / 2
    const path = `M ${leftX} ${cy} L ${cx} ${topY} L ${rightX} ${cy} L ${cx} ${botY} Z`

    const stroke = isSelected ? 'var(--accent)' : node.strokeColor
    const sw = isSelected ? node.strokeWidth * 1.5 : node.strokeWidth

    return (
        <g
            className={`gnode ${styles.gnode}`}
            data-node-id={node.id}
            onClick={onClick}
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
            style={{ cursor: 'pointer' }}
        >
            <path d={path} fill={node.fillColor} fillOpacity={0.9} stroke={stroke} strokeWidth={sw} />
            <text x={cx} y={cy - 2} fontSize={12} fontWeight={700}
                fill={node.textColor} textAnchor="middle" dominantBaseline="central">
                {anchors}×{spans}
            </text>
            <text x={cx} y={cy + 12} fontSize={8} fontWeight={500}
                fill={node.strokeColor} textAnchor="middle" opacity={0.75}>
                {anchors} anchors · {spans} spans
            </text>
            {node.displayName && (
                <text x={cx} y={topY - 4} fontSize={9} fontWeight={600}
                    fill={node.strokeColor} textAnchor="middle" opacity={0.85}>
                    {node.displayName}
                </text>
            )}
        </g>
    )
}

/**
 * Boundary entity chip — a single-line pill with the entity name
 * centred. Mirrors `SvgRenderer`'s CHIP shape. Used for SOURCE,
 * RESULT, and self-loop catalyst entities that stay visible in
 * ACTION_FLOW view.
 *
 * Supports double-click rename like other node shapes. When editing,
 * swaps the SVG `<text>` for a foreignObject input so the rename
 * flow is uniform across CIRCLE / ROUNDED_RECT / CHIP.
 */
function ChipShape({
    node, isSelected,
    onClick, onEnter, onLeave,
    onDoubleClickName,
    editing, editValue, setEditValue, commitEdit, setEditing, inputRef,
}: {
    node: SceneNode
    isSelected: boolean
    onClick: (e: React.MouseEvent) => void
    onEnter: () => void
    onLeave: () => void
    // Edit-related props are optional so the display-only NodeCardSimple
    // (Safari / iOS fallback) can render chips without rename support.
    onDoubleClickName?: (e: React.MouseEvent) => void
    editing?: 'name' | 'actor' | null
    editValue?: string
    setEditValue?: (v: string) => void
    commitEdit?: () => void
    setEditing?: (v: 'name' | 'actor' | null) => void
    inputRef?: React.RefObject<HTMLInputElement>
}) {
    const left = node.x - node.width / 2
    const top = node.y - node.height / 2
    const r = node.height / 2
    const stroke = isSelected ? 'var(--accent)' : node.strokeColor
    const sw = isSelected ? node.strokeWidth * 1.5 : node.strokeWidth
    const isEditing = editing === 'name'

    return (
        <g
            className={`gnode ${styles.gnode}`}
            data-node-id={node.id}
            onClick={onClick}
            onDoubleClick={onDoubleClickName}
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
            style={{ cursor: 'pointer' }}
        >
            <rect x={left} y={top} width={node.width} height={node.height}
                rx={r} ry={r}
                fill={node.fillColor} stroke={stroke} strokeWidth={sw} />
            {isEditing && inputRef && setEditValue && commitEdit && setEditing ? (
                <foreignObject x={left} y={top} width={node.width} height={node.height}>
                    <input
                        ref={inputRef}
                        className={styles.editInput}
                        value={editValue ?? ''}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={commitEdit}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') commitEdit()
                            if (e.key === 'Escape') setEditing(null)
                            e.stopPropagation()
                        }}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            width: '100%', height: '100%', boxSizing: 'border-box',
                            textAlign: 'center', fontSize: 11, fontWeight: 600,
                            color: node.strokeColor, background: 'transparent',
                            border: 'none', outline: 'none', padding: '0 8px',
                        }}
                    />
                </foreignObject>
            ) : (
                <text x={node.x} y={node.y} fontSize={11} fontWeight={600}
                    fill={node.strokeColor}
                    textAnchor="middle" dominantBaseline="central">
                    {node.displayName}
                </text>
            )}
        </g>
    )
}

/**
 * SOURCE / RESULT entity name annotations on the west / east side of an
 * action circle. Mirrors the input/output annotation rendering in
 * `SvgRenderer.renderNode`. Inputs stack upward from above the circle's
 * top; outputs stack downward from below the circle's bottom — keeping
 * them clear of the horizontal edge-approach band.
 */
function ActionAnnotations({ node }: { node: SceneNode }) {
    const inputs = node.inputAnnotations ?? []
    const outputs = node.outputAnnotations ?? []
    if (inputs.length === 0 && outputs.length === 0) return null

    const left = node.x - node.width / 2
    const top = node.y - node.height / 2
    const annotFontSize = 9
    const annotLineHeight = annotFontSize * 1.4
    const annotColor = node.strokeColor
    const charW = annotFontSize * 0.62
    const chipPadX = 4
    const chipPadY = 2
    const vClearance = 2

    const inputElements = inputs.map((name, i) => {
        const text = `${name} →`
        const chipW = text.length * charW + chipPadX * 2
        const chipH = annotFontSize + chipPadY * 2
        const stackIndex = inputs.length - 1 - i
        const y = top - vClearance - stackIndex * annotLineHeight - chipH / 2
        const rightEdge = left - 2
        return (
            <g key={`in-${i}`}>
                <rect x={rightEdge - chipW} y={y - chipH / 2} width={chipW} height={chipH}
                    rx={3} ry={3}
                    fill="var(--bg)" fillOpacity={0.92}
                    stroke={annotColor} strokeWidth={0.5} strokeOpacity={0.45} />
                <text x={rightEdge - chipPadX} y={y} fontSize={annotFontSize} fontWeight={500}
                    fill={annotColor} textAnchor="end" dominantBaseline="central">
                    {text}
                </text>
            </g>
        )
    })

    const outputElements = outputs.map((name, i) => {
        const text = `→ ${name}`
        const chipW = text.length * charW + chipPadX * 2
        const chipH = annotFontSize + chipPadY * 2
        const y = top + node.height + vClearance + i * annotLineHeight + chipH / 2
        const leftEdge = left + node.width + 2
        return (
            <g key={`out-${i}`}>
                <rect x={leftEdge} y={y - chipH / 2} width={chipW} height={chipH}
                    rx={3} ry={3}
                    fill="var(--bg)" fillOpacity={0.92}
                    stroke={annotColor} strokeWidth={0.5} strokeOpacity={0.45} />
                <text x={leftEdge + chipPadX} y={y} fontSize={annotFontSize} fontWeight={500}
                    fill={annotColor} textAnchor="start" dominantBaseline="central">
                    {text}
                </text>
            </g>
        )
    })

    return (
        <g style={{ pointerEvents: 'none' }}>
            {inputElements}
            {outputElements}
        </g>
    )
}

// ── NodeCard ─────────────────────────────────────────────────────────

export const NodeCard = memo(function NodeCard({
    node, isSelected, isHovered: _isHovered, isSimulating,
    onNodeClick, onNodeDoubleClick, onNodeEnter, onNodeLeave,
}: NodeCardProps) {
    // ALL hooks declared at the top — Rules of Hooks require the same
    // hook count and order on every render. Don't put hooks after any
    // conditional return below.
    const [editing, setEditing] = useState<'name' | 'actor' | null>(null)
    const [editValue, setEditValue] = useState('')
    const inputRef = useRef<HTMLInputElement>(null)

    const handleClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation()
        onNodeClick(node.id, e.shiftKey)
    }, [node.id, onNodeClick])
    const handleEnter = useCallback(() => onNodeEnter(node.id), [node.id, onNodeEnter])
    const handleLeave = useCallback(() => onNodeLeave(), [onNodeLeave])

    const handleDoubleClickName = useCallback((e: React.MouseEvent) => {
        e.stopPropagation()
        if (!onNodeDoubleClick) return
        setEditValue(node.displayName)
        setEditing('name')
    }, [node.displayName, onNodeDoubleClick])

    const handleDoubleClickActor = useCallback((e: React.MouseEvent) => {
        e.stopPropagation()
        if (!onNodeDoubleClick || !node.actorLabel) return
        setEditValue(node.actorLabel)
        setEditing('actor')
    }, [node.actorLabel, onNodeDoubleClick])

    const commitEdit = useCallback(() => {
        const field = editing
        setEditing(null)
        const trimmed = editValue.trim()
        if (!trimmed || !onNodeDoubleClick) return
        if (field === 'name' && trimmed !== node.displayName) {
            onNodeDoubleClick(node.id, trimmed)
        } else if (field === 'actor' && node.actorLabel && trimmed !== node.actorLabel) {
            onNodeDoubleClick(node.id, trimmed, node.actorLabel)
        }
    }, [editing, editValue, node.displayName, node.actorLabel, node.id, onNodeDoubleClick])

    useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current.focus()
            inputRef.current.select()
        }
    }, [editing])

    // Motif glyphs and chips: pure SVG, no foreignObject — short-circuit
    // AFTER all hooks have been declared.
    if (node.shape === 'FAN_OUT' || node.shape === 'FAN_IN') {
        return <FanGlyph node={node} isSelected={isSelected}
            onClick={handleClick} onEnter={handleEnter} onLeave={handleLeave} />
    }
    if (node.shape === 'CONNECTOR') {
        return <ConnectorGlyph node={node} isSelected={isSelected}
            onClick={handleClick} onEnter={handleEnter} onLeave={handleLeave} />
    }
    if (node.shape === 'CHIP') {
        return <ChipShape node={node} isSelected={isSelected}
            onClick={handleClick} onEnter={handleEnter} onLeave={handleLeave}
            onDoubleClickName={handleDoubleClickName}
            editing={editing} editValue={editValue}
            setEditValue={setEditValue} commitEdit={commitEdit}
            setEditing={setEditing} inputRef={inputRef} />
    }

    // CIRCLE / ROUNDED_RECT path uses foreignObject (existing behaviour)
    const left = node.x - node.width / 2
    const top = node.y - node.height / 2
    const roleColor = node.strokeColor
    const hasActor = !!node.actorLabel
    const isAction = node.shape === 'CIRCLE' || node.semanticRole === 'ACTION' || hasActor
    const isEntity = !isAction

    const classNames = [
        styles.gnode,
        'gnode',
        isAction && styles.circle,
        isEntity && styles.entity,
        isSelected && styles.selected,
        node.isBridge && styles.bridge,
    ].filter(Boolean).join(' ')

    const glowFilter = node.isBridge && isSimulating ? 'url(#glow-bridge)' : undefined

    // Editorial focal ring — accent outer ring on HUB + bridge nodes. The
    // ring sits behind the main shape (painted first in the <g>) and extends
    // ~3.5px beyond the node's footprint. Accent is pulled from the live CSS
    // theme so it tracks light/dark + seed-picker changes.
    const ringOffset = 3.5
    const focalRing = node.isFocal === true ? (
        isAction ? (
            <circle cx={node.x} cy={node.y} r={node.width / 2 + ringOffset}
                fill="none" stroke="var(--accent)" strokeWidth={1.5} strokeOpacity={0.55} />
        ) : (
            <rect x={left - ringOffset} y={top - ringOffset}
                width={node.width + ringOffset * 2} height={node.height + ringOffset * 2}
                rx={(node.cornerRadius ?? 6) + ringOffset} ry={(node.cornerRadius ?? 6) + ringOffset}
                fill="none" stroke="var(--accent)" strokeWidth={1.5} strokeOpacity={0.55} />
        )
    ) : null

    return (
        <g filter={glowFilter}>
            {focalRing}
            <foreignObject x={left} y={top} width={node.width} height={node.height}>
                <div
                    className={classNames}
                    style={{
                        '--gnode-clr': roleColor,
                        background: isSelected
                            ? `color-mix(in srgb, ${roleColor} 15%, ${node.fillColor})`
                            : node.fillColor,
                        borderColor: roleColor,
                        borderWidth: 'var(--node-border-width, 1px)',
                        borderRadius: isAction ? '50%' : 'var(--node-corner-radius, 6px)',
                        // Clip hit-area to the visible shape. foreignObject's
                        // bounding rect used to cover the whole node bbox,
                        // so corners of circles/rounded-rects stole pointer
                        // events meant for canvas pan. clip-path also clips
                        // hit-testing — now the dead space between shapes
                        // actually IS dead.
                        clipPath: isAction ? 'circle(50%)' : 'inset(0 round var(--node-corner-radius, 6px))',
                    } as React.CSSProperties}
                    data-node-id={node.id}
                    onClick={(e) => onNodeClick(node.id, e.shiftKey)}
                    onDoubleClick={handleDoubleClickName}
                    onMouseEnter={() => onNodeEnter(node.id)}
                    onMouseLeave={onNodeLeave}
                >
                    <div className={styles.content}>
                        {isEntity ? (
                            <>
                                <div className={styles.label} style={{ color: isDarkFill(node.fillColor) ? node.textColor : roleColor }}>
                                    {(node.topLabel ?? node.semanticRole).toLowerCase()}
                                </div>
                                {editing === 'name' ? (
                                    <input ref={inputRef} className={styles.editInput} value={editValue} onChange={(e) => setEditValue(e.target.value)} onBlur={commitEdit} onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditing(null); e.stopPropagation() }} onClick={(e) => e.stopPropagation()} />
                                ) : (
                                    <div className={styles.name} style={{ color: node.textColor }}>{titleCase(node.displayName)}</div>
                                )}
                            </>
                        ) : (
                            <>
                                {editing === 'actor' ? (
                                    <input ref={inputRef} className={styles.editInput} value={editValue} onChange={(e) => setEditValue(e.target.value)} onBlur={commitEdit} onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditing(null); e.stopPropagation() }} onClick={(e) => e.stopPropagation()} style={{ fontSize: 8 }} />
                                ) : (
                                    <div className={styles.actor} style={{ color: isDarkFill(node.fillColor) ? node.textColor : roleColor, cursor: 'pointer' }} onDoubleClick={handleDoubleClickActor}>
                                        {(node.actorLabel ?? 'action').toLowerCase()}
                                    </div>
                                )}
                                {editing === 'name' ? (
                                    <input ref={inputRef} className={styles.editInput} value={editValue} onChange={(e) => setEditValue(e.target.value)} onBlur={commitEdit} onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditing(null); e.stopPropagation() }} onClick={(e) => e.stopPropagation()} />
                                ) : (
                                    <div className={styles.name} style={{ color: node.textColor }}>{titleCase(node.displayName)}</div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </foreignObject>
            {/* Phase 1 boundary entity annotations on action circles */}
            {isAction && <ActionAnnotations node={node} />}
        </g>
    )
})

/**
 * Pure SVG node — no foreignObject. Works on all browsers including Safari.
 * Used as LOD fallback and as primary renderer on Safari/iOS.
 */
export const NodeCardSimple = memo(function NodeCardSimple({
    node, isSelected,
}: { node: SceneNode; isSelected: boolean }) {
    // Motif glyphs and chips: same SVG path as the full NodeCard.
    if (node.shape === 'FAN_OUT' || node.shape === 'FAN_IN') {
        return <FanGlyph node={node} isSelected={isSelected}
            onClick={() => {}} onEnter={() => {}} onLeave={() => {}} />
    }
    if (node.shape === 'CONNECTOR') {
        return <ConnectorGlyph node={node} isSelected={isSelected}
            onClick={() => {}} onEnter={() => {}} onLeave={() => {}} />
    }
    if (node.shape === 'CHIP') {
        return <ChipShape node={node} isSelected={isSelected}
            onClick={() => {}} onEnter={() => {}} onLeave={() => {}} />
    }

    const left = node.x - node.width / 2
    const top = node.y - node.height / 2
    const color = node.strokeColor
    const hasActor = !!node.actorLabel
    const isAction = node.shape === 'CIRCLE' || node.semanticRole === 'ACTION' || hasActor
    const isEntity = !isAction
    const actorName = hasActor ? (node.actorLabel ?? '').toLowerCase() : ''

    return (
        <g className="gnode">
            {isAction ? (
                <circle
                    cx={node.x} cy={node.y} r={node.width / 2}
                    fill={node.fillColor}
                    stroke={isSelected ? 'var(--accent)' : color}
                    strokeWidth={isSelected ? node.strokeWidth * 2 : node.strokeWidth}
                />
            ) : (
                <rect
                    x={left} y={top} width={node.width} height={node.height}
                    rx={node.cornerRadius} ry={node.cornerRadius}
                    fill={node.fillColor}
                    stroke={isSelected ? 'var(--accent)' : color}
                    strokeWidth={isSelected ? node.strokeWidth * 2 : node.strokeWidth}
                />
            )}
            {isEntity ? (
                <>
                    <text
                        x={left + 8} y={node.y - 6}
                        fontSize={8} fontWeight={500}
                        fill={color} opacity={0.7}
                        dominantBaseline="central"
                    >
                        {(node.topLabel ?? node.semanticRole).toLowerCase()}
                    </text>
                    <text
                        x={left + 8} y={node.y + 9}
                        fontSize={12} fontWeight={700}
                        fill={node.textColor}
                        dominantBaseline="central"
                    >
                        {titleCase(node.displayName).slice(0, 22)}
                    </text>
                </>
            ) : (
                <>
                    {actorName && (() => {
                        const pillH = 14
                        const gap = 8
                        const contentH = pillH + gap + 12
                        const contentTop = node.y - contentH / 2
                        return (
                            <text
                                x={node.x} y={contentTop + pillH / 2}
                                fontSize={8} fontWeight={600}
                                fill={color} dominantBaseline="central"
                                textAnchor="middle"
                            >
                                {actorName.slice(0, 20)}
                            </text>
                        )
                    })()}
                    <text
                        x={node.x} y={(() => {
                            if (!actorName) return node.y + 4
                            const contentH = 14 + 8 + 12
                            const contentTop = node.y - contentH / 2
                            return contentTop + 14 + 8 + 12 * 0.35
                        })()}
                        fontSize={12} fontWeight={700}
                        fill={node.textColor} opacity={0.9}
                        dominantBaseline="central"
                        textAnchor="middle"
                    >
                        {titleCase(node.displayName).slice(0, 20)}
                    </text>
                </>
            )}
            {isAction && <ActionAnnotations node={node} />}
        </g>
    )
})
